<template>
  <div class="text-left pb-10">
    <h1 class="font-weight-black text-h4 my-4">Convives de la cantine</h1>
    <p class="body-1">
      Les convives sont rattachés directement, sans code à confirmer. Vous pouvez les ajouter un à un ou importer un
      fichier. Seules l'adresse électronique et le prénom sont nécessaires.
    </p>

    <!-- Réglage de l'écran servi au convive (flag show_dashboard, propriété de consumer-dashboard-cards) -->
    <v-sheet outlined rounded class="pa-4 mb-6">
      <v-switch
        v-model="showDashboard"
        :loading="savingFlag"
        :disabled="savingFlag"
        inset
        hide-details
        class="mt-0"
        label="Afficher le tableau de bord aux convives (sinon, mode parcours pédagogique)"
        @change="onToggleDashboard"
      />
    </v-sheet>

    <!-- Liste des convives rattachés -->
    <v-list v-if="consumers.length > 0">
      <ConsumerItem @delete="deleteConsumer" v-for="consumer in consumers" :key="consumer.id" :consumer="consumer" />
    </v-list>
    <p v-else class="grey--text mt-4">Aucun convive pour l'instant.</p>

    <!-- Ajout direct d'un convive -->
    <v-row class="mt-6">
      <v-col cols="12" sm="11" md="9">
        <v-form ref="consumerForm" v-model="formIsValid" v-on:submit.prevent="addConsumer">
          <p class="body-2 mb-2 text-left grey--text text--darken-1">Ajouter un convive</p>
          <div class="d-sm-flex align-sm-start flex-wrap">
            <DsfrEmail
              v-model="newEmail"
              label="Adresse électronique"
              :rules="[validators.emailOrEmpty]"
              class="flex-grow-1 mb-sm-6 mr-sm-4"
            />
            <v-text-field
              v-model="newFirstName"
              label="Prénom (facultatif)"
              outlined
              dense
              class="mb-sm-6 mr-sm-4"
              style="max-width: 200px;"
            />
            <v-btn @click="addConsumer" outlined color="primary darken-1" large :disabled="!newEmail">Ajouter</v-btn>
          </div>
        </v-form>
      </v-col>
    </v-row>

    <!-- Import de masse CSV / Excel -->
    <v-divider class="my-6" aria-hidden="true" role="presentation" />
    <p class="body-2 mb-2 text-left grey--text text--darken-1">Importer une liste (CSV ou Excel)</p>
    <p class="caption grey--text text--darken-1 mb-2">
      Le fichier doit comporter une colonne
      <strong>email</strong>
      (obligatoire) ; les colonnes
      <strong>prénom</strong>
      et
      <strong>nom</strong>
      sont facultatives.
    </p>
    <div class="d-sm-flex align-sm-center flex-wrap">
      <v-file-input
        v-model="importFile"
        label="Fichier de convives"
        accept=".csv,.xlsx,.xls"
        outlined
        dense
        hide-details
        class="flex-grow-1 mb-sm-0 mr-sm-4"
        style="max-width: 400px;"
      />
      <v-btn @click="submitImport" outlined color="primary darken-1" large :loading="importing" :disabled="!importFile">
        Importer
      </v-btn>
    </div>

    <!-- Compte rendu d'import (jamais de donnée personnelle) -->
    <v-alert v-if="importReport" :type="importReport.rejected.length ? 'warning' : 'success'" text class="mt-4">
      <p class="mb-1 font-weight-medium">
        {{ importReport.accepted }} convive{{ importReport.accepted > 1 ? "s" : "" }} ajouté{{
          importReport.accepted > 1 ? "s" : ""
        }}
        ({{ importReport.created }} créé{{ importReport.created > 1 ? "s" : "" }},
        {{ importReport.attached }} rattaché{{ importReport.attached > 1 ? "s" : "" }}).
      </p>
      <p v-if="importReport.already" class="mb-1">
        {{ importReport.already }} déjà rattaché{{ importReport.already > 1 ? "s" : "" }} (ignoré{{
          importReport.already > 1 ? "s" : ""
        }}).
      </p>
      <p v-if="importReport.rejected.length" class="mb-0">
        {{ importReport.rejected.length }} ligne{{ importReport.rejected.length > 1 ? "s" : "" }} rejetée{{
          importReport.rejected.length > 1 ? "s" : ""
        }}
        :
        <span v-for="(r, i) in importReport.rejected" :key="i">
          ligne {{ r.row }} ({{ r.reason }}){{ i < importReport.rejected.length - 1 ? ", " : "" }}
        </span>
      </p>
    </v-alert>
  </div>
</template>

<script>
import ConsumerItem from "./ConsumerItem"
import DsfrEmail from "@/components/DsfrEmail"
import validators from "@/validators"

export default {
  name: "CanteenConsumers",
  components: { ConsumerItem, DsfrEmail },
  props: {
    originalCanteen: {
      type: Object,
      required: true,
    },
  },
  created() {
    document.title = `Convives - ${this.originalCanteen.name} - ${this.$store.state.pageTitleSuffix}`
  },
  mounted() {
    this.loadConsumers()
  },
  data() {
    return {
      formIsValid: true,
      newEmail: null,
      newFirstName: null,
      showDashboard: this.originalCanteen.showDashboard,
      savingFlag: false,
      importFile: null,
      importing: false,
      importReport: null,
      // Les convives (avec e-mail) ne sont servis que par l'endpoint gestionnaire dédié, jamais par le
      // serializer de cantine — l'e-mail d'un convive reste hors des vues publiques (RGPD).
      consumers: [],
    }
  },
  computed: {
    validators() {
      return validators
    },
  },
  methods: {
    loadConsumers() {
      this.$store
        .dispatch("fetchConsumers", { canteenId: this.originalCanteen.id })
        .then((team) => {
          this.consumers = team["consumers"] || []
        })
        .catch((e) => {
          this.$store.dispatch("notifyServerError", e)
        })
    },
    addConsumer() {
      this.$refs.consumerForm.validate()
      if (!this.formIsValid) {
        this.$store.dispatch("notifyRequiredFieldsError")
        return
      }
      this.$store
        .dispatch("addConsumer", {
          canteenId: this.originalCanteen.id,
          email: this.newEmail.trim(),
          firstName: (this.newFirstName || "").trim(),
          lastName: "",
        })
        .then((team) => {
          this.$store.dispatch("notify", {
            title: "Convive ajouté",
            message: "Le convive a bien été rattaché à cette cantine.",
            status: "success",
          })
          this.newEmail = null
          this.newFirstName = null
          this.consumers = team["consumers"] || []
        })
        .catch((e) => {
          this.$store.dispatch("notifyServerError", e)
        })
    },
    deleteConsumer(consumer) {
      this.$store
        .dispatch("removeConsumer", {
          canteenId: this.originalCanteen.id,
          email: consumer.email.trim(),
        })
        .then((team) => {
          this.$store.dispatch("notify", {
            title: "Convive retiré",
            message: "Le convive n'est plus rattaché à cette cantine.",
            status: "success",
          })
          this.consumers = team["consumers"] || []
        })
        .catch((e) => {
          this.$store.dispatch("notifyServerError", e)
        })
    },
    onToggleDashboard(value) {
      this.savingFlag = true
      this.$store
        .dispatch("updateCanteen", {
          id: this.originalCanteen.id,
          payload: { show_dashboard: value },
        })
        .then((canteen) => {
          this.$emit("updateCanteen", canteen)
        })
        .catch((e) => {
          // Rétablir l'état affiché si l'enregistrement a échoué.
          this.showDashboard = !value
          this.$store.dispatch("notifyServerError", e)
        })
        .finally(() => {
          this.savingFlag = false
        })
    },
    submitImport() {
      if (!this.importFile) return
      this.importing = true
      this.importReport = null
      this.$store
        .dispatch("importConsumers", {
          canteenId: this.originalCanteen.id,
          file: this.importFile,
        })
        .then((report) => {
          this.importReport = report
          this.importFile = null
          // L'import ne renvoie que le compte rendu : on recharge la liste des convives.
          this.loadConsumers()
        })
        .catch((e) => {
          this.$store.dispatch("notifyServerError", e)
        })
        .finally(() => {
          this.importing = false
        })
    },
  },
}
</script>
